import React, { useState, useEffect } from 'react';
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { userService } from '../../services/api';
import './ResetPassword.css';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from URL (e.g., ?token=...)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('Invalid or expired reset link. Please request a new one.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    try {
      await userService.resetPassword({ token, newPassword: password });
      setSuccess(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. The link might be expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="reset-password-overlay"></div>

      <div className="reset-password-container">
        <div className="reset-password-card">
          {!success ? (
            <>
              <div className="reset-password-header">
                <div className="header-icon-wrapper">
                  <FiRefreshCw className="header-icon" />
                </div>
                <h2>Reset Password</h2>
                <p>Create a strong new password for your account.</p>
              </div>

              {error && (
                <div className="status-message error">
                  <FiAlertCircle /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="reset-password-form">
                <div className="input-field">
                  <label>New Password</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimum 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="eye-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                <div className="input-field">
                  <label>Confirm New Password</label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Repeat your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  className={`reset-btn ${loading ? 'loading' : ''}`} 
                  disabled={loading || !token}
                >
                  {loading ? <div className="spinner"></div> : 'Update Password'}
                </button>
              </form>
            </>
          ) : (
            <div className="success-view">
              <div className="success-icon-wrapper">
                <FiCheckCircle className="success-icon" />
              </div>
              <h2>Password Updated!</h2>
              <p>Your password has been changed successfully. You will be redirected to the login page shortly.</p>
              <div className="auto-redirect-timer">
                <div className="timer-bar"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

